<template>
  <div class="homepage-container">
    <!-- Navigation -->
    <base-navigation></base-navigation>

    <!-- Hero Section -->
    <div class="hero-section bg-light py-5">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6 mb-4 mb-lg-0">
            <div class="hero-content">
              <div class="badge bg-primary text-white mb-3 p-2 rounded-pill d-inline-block">
                2025/26 Applications Closed
              </div>
              <h1 class="display-4 fw-bold mb-3">
                North Mugirango <span class="text-primary">Online Bursary</span> Portal
              </h1>
              <p class="lead mb-4">
                Apply for bursary online, download your form, and submit at designated collection centers.
                Fast, simple, and convenient for all students.
              </p>
              
              <div class="d-flex flex-wrap gap-3 mb-4">
                <span class="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 p-2">
                  <i class="bi bi-phone me-1"></i> Mobile Friendly
                </span>
                <span class="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 p-2">
                  <i class="bi bi-lightning me-1"></i> Quick Process
                </span>
                <span class="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25 p-2">
                  <i class="bi bi-award me-1"></i> All Institutions
                </span>
              </div>
              
              <div class="d-flex flex-wrap gap-3">
                <router-link to="/secondary/application" class="btn btn-primary btn-lg px-4">
                  <i class="bi bi-mortarboard me-2"></i> Secondary School
                </router-link>
                <router-link to="/college/application" class="btn btn-outline-primary btn-lg px-4">
                  <i class="bi bi-building me-2"></i> University & College
                </router-link>
              </div>
            </div>
          </div>
          
          <div class="col-lg-6">
            <div class="hero-stats bg-white rounded shadow p-4">
              <div class="row text-center">
                <div class="col-4 mb-3">
                  <div class="stat-number text-primary fw-bold fs-2">30K+</div>
                  <div class="stat-label text-muted small">Students Helped</div>
                </div>
                <div class="col-4 mb-3">
                  <div class="stat-number text-success fw-bold fs-2">98%</div>
                  <div class="stat-label text-muted small">Success Rate</div>
                </div>
                <div class="col-4 mb-3">
                  <div class="stat-number text-info fw-bold fs-2">5</div>
                  <div class="stat-label text-muted small">Simple Steps</div>
                </div>
              </div>
              <div class="text-center mt-3">
                <div class="deadline-alert alert alert-warning mb-0">
                  <i class="bi bi-clock me-2"></i>
                  <strong>Deadline:</strong> 16th Jan 2026 . ITS NOW CLOSED
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Links -->
    <div class="quick-links py-5 bg-white">
      <div class="container">
        <h2 class="text-center mb-4 fw-bold">Get Started Quickly</h2>
        <div class="row g-4">
          <div class="col-md-3 col-sm-6">
            <div class="card h-100 border-0 shadow-sm hover-lift">
              <div class="card-body text-center p-4">
                <div class="icon-wrapper bg-primary bg-opacity-10 rounded-circle p-3 mb-3 mx-auto" style="width: 60px; height: 60px;">
                  <i class="bi bi-pencil-square text-primary fs-3"></i>
                </div>
                <h5 class="card-title">New Application</h5>
                <p class="card-text text-muted small">Start a fresh bursary application</p>
                <router-link to="/apply/cdf" class="btn btn-outline-primary btn-sm mt-2">
                  Apply Now <i class="bi bi-arrow-right ms-1"></i>
                </router-link>
              </div>
            </div>
          </div>
          
          <div class="col-md-3 col-sm-6">
            <div class="card h-100 border-0 shadow-sm hover-lift">
              <div class="card-body text-center p-4">
                <div class="icon-wrapper bg-success bg-opacity-10 rounded-circle p-3 mb-3 mx-auto" style="width: 60px; height: 60px;">
                  <i class="bi bi-download text-success fs-3"></i>
                </div>
                <h5 class="card-title">Download Form</h5>
                <p class="card-text text-muted small">Get your filled application form</p>
                <router-link to="/application/form/" class="btn btn-outline-success btn-sm mt-2">
                  Download <i class="bi bi-arrow-down ms-1"></i>
                </router-link>
              </div>
            </div>
          </div>
          
          <div class="col-md-3 col-sm-6">
            <div class="card h-100 border-0 shadow-sm hover-lift">
              <div class="card-body text-center p-4">
                <div class="icon-wrapper bg-info bg-opacity-10 rounded-circle p-3 mb-3 mx-auto" style="width: 60px; height: 60px;">
                  <i class="bi bi-clipboard-check text-info fs-3"></i>
                </div>
                <h5 class="card-title">Check Status</h5>
                <p class="card-text text-muted small">Track your application progress</p>
                <router-link to="/" class="btn btn-outline-info btn-sm mt-2">
                  Check <i class="bi bi-search ms-1"></i>
                </router-link>
              </div>
            </div>
          </div>
          
          <div class="col-md-3 col-sm-6">
            <div class="card h-100 border-0 shadow-sm hover-lift">
              <div class="card-body text-center p-4">
                <div class="icon-wrapper bg-warning bg-opacity-10 rounded-circle p-3 mb-3 mx-auto" style="width: 60px; height: 60px;">
                  <i class="bi bi-info-circle text-warning fs-3"></i>
                </div>
                <h5 class="card-title">Requirements</h5>
                <p class="card-text text-muted small">View required documents</p>
                <a href="#requirements" class="btn btn-outline-warning btn-sm mt-2">
                  View <i class="bi bi-eye ms-1"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Process Section -->
    <div class="process-section py-5 bg-light">
      <div class="container">
        <h2 class="text-center mb-5 fw-bold">Simple 5-Step Process</h2>
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <div class="process-steps">
              <div class="process-step mb-4" v-for="(step, index) in processSteps" :key="index">
                <div class="d-flex align-items-start">
                  <div class="step-number bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" 
                       style="width: 40px; height: 40px; flex-shrink: 0;">
                    {{ index + 1 }}
                  </div>
                  <div class="step-content">
                    <h5 class="fw-bold mb-2">{{ step.title }}</h5>
                    <p class="text-muted mb-0">{{ step.description }}</p>
                  </div>
                </div>
                <div class="step-connector ms-4 ps-3" v-if="index < processSteps.length - 1">
                  <div class="border-start border-2 border-primary" style="height: 40px; margin-left: 18px;"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Requirements Section -->
    <div class="requirements-section py-5 bg-white" id="requirements">
      <div class="container">
        <h2 class="text-center mb-5 fw-bold">Required Documents</h2>
        <div class="row justify-content-center">
          <div class="col-lg-8">
            <div class="list-group">
              <div class="list-group-item border-0 py-3" v-for="(doc, index) in requiredDocuments" :key="index">
                <div class="d-flex align-items-center">
                  <div class="badge bg-primary rounded-circle me-3" style="width: 30px; height: 30px; line-height: 30px;">
                    {{ index + 1 }}
                  </div>
                  <span>{{ doc }}</span>
                </div>
              </div>
            </div>
            
            <div class="alert alert-warning mt-4">
              <div class="d-flex">
                <i class="bi bi-exclamation-triangle-fill me-3 fs-4"></i>
                <div>
                  <strong>Important:</strong> All documents must be photocopies and properly attached to your 
                  application form before submission to the collection centers.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Collection Centers -->
    <div class="centers-section py-5 bg-light">
      <div class="container">
        <h2 class="text-center mb-5 fw-bold">Collection Centers</h2>
        <div class="row g-4">
          <div class="col-md-4" v-for="center in collectionCenters" :key="center.ward">
            <div class="card h-100 border-0 shadow-sm">
              <div class="card-body">
                <h5 class="card-title fw-bold text-primary">{{ center.ward }}</h5>
                <ul class="list-unstyled mb-3">
                  <li v-for="location in center.locations" :key="location" class="mb-2">
                    <i class="bi bi-geo-alt text-muted me-2"></i>
                    {{ location }}
                  </li>
                </ul>
                <div class="text-muted small">
                  <i class="bi bi-clock me-1"></i>
                  {{ center.hours }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contact Section -->
    <div class="contact-section py-5 bg-white">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6 mb-4 mb-lg-0">
            <h2 class="fw-bold mb-4">Need Assistance?</h2>
            <div class="contact-info">
              <div class="d-flex align-items-center mb-3">
                <div class="icon-wrapper bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                  <i class="bi bi-telephone text-primary fs-4"></i>
                </div>
                <div>
                  <h5 class="fw-bold mb-1">Call Us</h5>
                  <a href="tel:+254725029795" class="text-decoration-none">0725 029 795</a>
                  <div class="text-muted small">8:00 AM - 7:00 PM, Monday-Friday</div>
                </div>
              </div>
              
              <div class="d-flex align-items-center mb-3">
                <div class="icon-wrapper bg-success bg-opacity-10 rounded-circle p-3 me-3">
                  <i class="bi bi-envelope text-success fs-4"></i>
                </div>
                <div>
                  <h5 class="fw-bold mb-1">Email</h5>
                  <a href="mailto:ngcdfnorthmugirango@ngcdf.go.ke" class="text-decoration-none">
                    ngcdfnorthmugirango@ngcdf.go.ke
                  </a>
                </div>
              </div>
              
              <div class="d-flex align-items-center">
                <div class="icon-wrapper bg-info bg-opacity-10 rounded-circle p-3 me-3">
                  <i class="bi bi-geo-alt text-info fs-4"></i>
                </div>
                <div>
                  <h5 class="fw-bold mb-1">Visit Office</h5>
                  <div>D.C.C. Building, Ground Floor, Ekerenyo</div>
                  <div class="text-muted small">North Mugirango Constituency</div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-lg-6">
            <div class="card border-0 shadow">
              <div class="card-body p-4">
                <h4 class="fw-bold mb-3">Quick Inquiry</h4>
                <form @submit.prevent="sendInquiry">
                  <div class="mb-3">
                    <input type="text" class="form-control" placeholder="Your Name" required>
                  </div>
                  <div class="mb-3">
                    <input type="tel" class="form-control" placeholder="Phone Number" required>
                  </div>
                  <div class="mb-3">
                    <textarea class="form-control" placeholder="Your Question..." rows="3" required></textarea>
                  </div>
                  <button type="submit" class="btn btn-primary w-100">
                    <i class="bi bi-send me-2"></i> Send Inquiry
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Final CTA -->
    <div class="cta-section py-5 bg-primary text-white">
      <div class="container text-center">
        <h2 class="fw-bold mb-3">Ready to Apply?</h2>
        <p class="lead mb-4">Join thousands of students who have successfully applied online</p>
        <div class="d-flex flex-wrap justify-content-center gap-3">
          <router-link to="/secondary/application" class="btn btn-light btn-lg px-4">
            <i class="bi bi-mortarboard me-2"></i> Secondary School
          </router-link>
          <router-link to="/college/application" class="btn btn-outline-light btn-lg px-4">
            <i class="bi bi-building me-2"></i> University & College
          </router-link>
        </div>
        <p class="mt-4 mb-0">
          Already applied? 
          <router-link to="/application/form/" class="text-white text-decoration-underline fw-bold">
            Download your form here
          </router-link>
        </p>
      </div>
    </div>

    <!-- Footer -->
    <footer class="footer bg-dark text-white py-5">
      <div class="container">
        <div class="row">
          <div class="col-lg-4 mb-4 mb-lg-0">
            <h4 class="fw-bold mb-3">North Mugirango Bursary</h4>
            <p class="text-white-50">Empowering students through digital bursary applications</p>
            <div class="text-white-50 small">
              <div class="mb-1">
                <i class="bi bi-envelope me-2"></i> ngcdfnorthmugirango@ngcdf.go.ke
              </div>
              <div class="mb-1">
                <i class="bi bi-telephone me-2"></i> 0725 029 795
              </div>
              <div>
                <i class="bi bi-geo-alt me-2"></i> N.C.C. Building, Ekerenyo
              </div>
            </div>
          </div>
          
          <div class="col-lg-8">
            <div class="row">
              <div class="col-md-4 mb-4 mb-md-0">
                <h5 class="fw-bold mb-3">Applications</h5>
                <ul class="list-unstyled">
                  <li class="mb-2">
                    <router-link to="/secondary/application" class="text-white-50 text-decoration-none">
                      Secondary School
                    </router-link>
                  </li>
                  <li class="mb-2">
                    <router-link to="/college/application" class="text-white-50 text-decoration-none">
                      College & University
                    </router-link>
                  </li>
                  <li>
                    <router-link to="/apply/cdf" class="text-white-50 text-decoration-none">
                      Application Guide
                    </router-link>
                  </li>
                </ul>
              </div>
              
              <div class="col-md-4 mb-4 mb-md-0">
                <h5 class="fw-bold mb-3">Resources</h5>
                <ul class="list-unstyled">
                  <li class="mb-2">
                    <router-link to="/application/form/" class="text-white-50 text-decoration-none">
                      Download Form
                    </router-link>
                  </li>
                  <li class="mb-2">
                    <a href="#requirements" class="text-white-50 text-decoration-none">
                      Requirements
                    </a>
                  </li>
                  <li>
                    <a href="#process" class="text-white-50 text-decoration-none">
                      Process Steps
                    </a>
                  </li>
                </ul>
              </div>
              
              <div class="col-md-4">
                <h5 class="fw-bold mb-3">Support</h5>
                <ul class="list-unstyled">
                  <li class="mb-2">
                    <a href="tel:+254725029795" class="text-white-50 text-decoration-none">
                      Contact Support
                    </a>
                  </li>
                  <li class="mb-2">
                    <a href="mailto:ngcdfnorthmugirango@ngcdf.go.ke" class="text-white-50 text-decoration-none">
                      Email Support
                    </a>
                  </li>
                  <li>
                    <router-link to="/" class="text-white-50 text-decoration-none">
                      FAQs
                    </router-link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        <hr class="bg-white-50 my-4">
        
        <div class="text-center text-white-50">
          <p class="mb-0">
            &copy; 2024 North Mugirango Constituency NG-CDF. All rights reserved.
          </p>
          <p class="small mt-2 mb-0">
            Digital Bursary Application System v2.0
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: "HomePage",
  data() {
    return {
      processSteps: [
        {
          title: "Fill Online Form",
          description: "Complete the digital application with student and parent details"
        },
        {
          title: "Download Form",
          description: "Generate and download your filled application form"
        },
        {
          title: "Get Verified",
          description: "Take form to local chief for verification and stamp"
        },
        {
          title: "Attach Documents",
          description: "Gather and attach all required supporting documents"
        },
        {
          title: "Submit Application",
          description: "Drop completed application at designated collection center"
        }
      ],
      requiredDocuments: [
        "Student's transcript/report form",
        "Photocopy of parent/guardian's ID card",
        "Student's National ID or Birth Certificate",
        "School/institution admission letter",
        "Chief/ ass. chief's verification stamp on the form"
      ],
      collectionCenters: [
        {
          ward: "Ekerenyo Ward",
          locations: ["Ekerenyo, Constituency office", "Ikonge market offices"],
          hours: "Monday-Friday: 8:00 AM - 5:00 PM"
        },
        {
          ward: "Bomwagamo Ward",
          locations: ["Kioge/Eaka Tea Buying centre", "Nyageita Tea Buying centre"],
          hours: "Monday-Friday: 8:00 AM - 5:00 PM"
        },
        {
          ward: "Itibo Ward",
          locations: ["Nyabonge/Getare Village Polytechnic", "Isinta Tea Buying centre"],
          hours: "Monday-Friday: 8:00 AM - 4:00 PM"
        },
        {
          ward: "Bokeira Ward",
          locations: ["Kapsuser/Omosaria shopping centre", "Nyamusi ACC/Police grounds"],
          hours: "Monday-Friday: 8:00 AM - 4:00 PM"
        },
        {
          ward: "Magwagwa Ward",
          locations: ["Magwagwa Farmers SACCO offices", "Karota Chief's office"],
          hours: "Monday-Friday: 8:00 AM - 4:00 PM"
        }
      ]
    };
  },
  methods: {
    sendInquiry() {
      alert("Thank you for your inquiry! We will contact you within 24 hours.");
    }
  },
  mounted() {
    // Add Bootstrap icons if not already loaded
    if (!document.querySelector('link[href*="bootstrap-icons"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.8.1/font/bootstrap-icons.css';
      document.head.appendChild(link);
    }
  }
};
</script>

<style scoped>
.homepage-container {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Simple hover effects */
.hover-lift {
  transition: transform 0.2s ease-in-out;
}

.hover-lift:hover {
  transform: translateY(-5px);
}

/* Step connector for process */
.step-connector {
  position: relative;
}

.step-connector .border-start {
  opacity: 0.3;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .display-4 {
    font-size: 2rem;
  }
  
  .lead {
    font-size: 1rem;
  }
  
  .hero-stats {
    margin-top: 2rem;
  }
}

/* Ensure proper spacing */
.card {
  border-radius: 10px;
}

.icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Custom button styles */
.btn-outline-light:hover {
  color: #0d6efd;
}

/* Footer link hover effect */
.footer a:hover {
  color: white !important;
  text-decoration: underline !important;
}

/* Text utilities */
.text-decoration-underline {
  text-decoration: underline;
}

.bg-opacity-10 {
  opacity: 0.1;
}

/* Fix for Bootstrap 5 spacing */
.gap-3 > * {
  margin: 0.5rem;
}

/* Make sure badges look good */
.badge {
  font-weight: 500;
}

/* Responsive process steps */
@media (max-width: 576px) {
  .process-step .step-number {
    width: 35px;
    height: 35px;
    font-size: 0.9rem;
  }
  
  .step-connector {
    margin-left: 0;
    padding-left: 0;
  }
  
  .step-connector .border-start {
    margin-left: 17px;
  }
}
</style>